/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import styled from 'styled-components';

type ModelGuidanceCardProps = {
    rightTitle: string;
    errorTitle: string;
    issuesMessage: string;
    numberOfIssues: number;
    errorDescription: string;
    onViewDetailsClick: () => void;
};

const messages = defineMessages({
    viewDetails: {
        id: 'modelGuidance.modelGuidanceCard.viewDetails',
        defaultMessage: 'View Details'
    },
    viewDetailsAriaLabel: {
        id: 'modelGuidance.modelGuidanceCard.viewDetailsAriaLabel',
        defaultMessage: 'View Details about {cardTitle}'
    },
    guidanceCardAriaLabel: {
        id: 'modelGuidance.modelGuidanceCard.guidanceCard',
        defaultMessage: '{cardTitle} guidance card, the number of issues is {numberOfIssues}'
    }
});

const theme = getScopedTheme('ModelGuidanceCard');

const Card = styled(Stack)({
    flex: 1,
    padding: '24px',
    borderWidth: '1px',
    borderStyle: 'solid',
    boxShadow: theme.boxShadow,
    borderColor: theme.borderColor
});

const CardTitleSection = styled(Stack)<{ numberOfIssues: number; isXLargeOrGreater: boolean }>(props => ({
    width: props.isXLargeOrGreater ? (props.numberOfIssues > 0 ? '55%' : '84%') : '100%',
    textAlign: props.isXLargeOrGreater ? 'start' : 'center'
}));

const CardTitle = styled(LuisText)<{ numberOfIssues: number }>(props => ({
    fontSize: '18px',
    lineHeight: '24px',
    fontWeight: props.numberOfIssues === 0 ? 400 : 600
}));

const CardDescription = styled(LuisText)({
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px'
});

const CardIcon = styled(Icon)<{ color: string }>(props => ({
    marginRight: '19px',
    color: props.color
}));

const IssuesNumberMessage = styled(LuisText)<{ isXLargeOrGreater: boolean }>(props => ({
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '22px',
    textAlign: 'center',
    width: props.isXLargeOrGreater ? '28.5%' : '100%'
}));

const ViewDetailsLinkSection = styled(ExternalLink)<{ isXLargeOrGreater: boolean }>(props => ({
    width: props.isXLargeOrGreater ? '15%' : '100%',
    textAlign: props.isXLargeOrGreater ? 'start' : 'center'
}));

const getIconColor = (numberOfIssues: number) => {
    if (numberOfIssues === 0) {
        return theme.checkMarkIconColor;
    }

    if (numberOfIssues < 3) {
        return theme.warningIconColor;
    }

    return theme.errorIconColor;
};

export const ModelGuidanceCard = (props: ModelGuidanceCardProps) => {
    const { rightTitle, errorTitle, issuesMessage, numberOfIssues, errorDescription, onViewDetailsClick } = props;

    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();

    const cardTitle = React.useMemo(() => (numberOfIssues === 0 ? rightTitle : errorTitle), [numberOfIssues, rightTitle, errorTitle]);

    return (
        <Card
            tabIndex={0}
            verticalAlign="center"
            tokens={{ childrenGap: 25 }}
            horizontal={isXLargeOrGreater}
            horizontalAlign={isXLargeOrGreater ? 'start' : 'center'}
            aria-label={localize(messages.guidanceCardAriaLabel, { cardTitle, numberOfIssues })}>
            <CardTitleSection horizontal verticalAlign="center" numberOfIssues={numberOfIssues} isXLargeOrGreater={isXLargeOrGreater}>
                <CardIcon
                    color={getIconColor(numberOfIssues)}
                    iconName={numberOfIssues === 0 ? 'CheckMark' : numberOfIssues < 3 ? 'warning' : 'infoSolid'}
                />

                <Stack>
                    <CardTitle numberOfIssues={numberOfIssues}>{cardTitle}</CardTitle>

                    {numberOfIssues > 0 && <CardDescription>{errorDescription}</CardDescription>}
                </Stack>
            </CardTitleSection>

            {numberOfIssues > 0 && <IssuesNumberMessage isXLargeOrGreater={isXLargeOrGreater}>{issuesMessage}</IssuesNumberMessage>}

            <ViewDetailsLinkSection
                isUnderlined={false}
                onClick={onViewDetailsClick}
                isXLargeOrGreater={isXLargeOrGreater}
                aria-label={localize(messages.viewDetailsAriaLabel, { cardTitle })}>
                {localize(messages.viewDetails)}
            </ViewDetailsLinkSection>
        </Card>
    );
};
